<template>
  <div>
    <page-header
      :page="pageConfig"
      left-side-class="col-md-4 col-sm-12"
      right-side-class="col-md-8 col-sm-12"
      class="page-header__extended"
    >
      <div class="d-flex">
        <action-button
          i="fas fa-arrow-circle-left"
          text="Regresar"
          variant="outline-success"
          @click="$router.go(-1)"
        />
        <action-button
          i="fas fa-print"
          text="Imprimir"
          variant="outline-success ml-2"
          @click="handlePrint"
        />
        <action-button
          v-if="isNewStatus"
          i="fas fa-save"
          text="Guardar"
          variant="success ml-2"
          @click="saveReportHandler"
        />
        <action-button
          variant="success ml-2"
          v-if="isNewStatus"
          i="fa-solid fa-flag"
          text="Someter"
          @click="handleSubmit"
        />
        <action-button
          v-if="showBuildFilesButton"
          variant="outline-success"
          class="ml-2"
          i="fas fa-arrows-rotate pr-2"
          text="Generar Archivos"
          @click="handleBuildFiles"
        />
        <action-button
          v-if="showAmendButton"
          variant="outline-success ml-2"
          i="fa fa-files-medical"
          text="Enmendar"
          class-icon="mx-0 no-margin"
          @click="handleAmend"
        />
        <action-button
          v-if="showPublishButton"
          variant="success"
          class="ml-2"
          i="fas fa-flag"
          text="Publicar"
          @click="handlePublish"
        />
      </div>
    </page-header>

    <board :details="detailsBoard" />

    <custom-tab
      class="mt-2"
      content-class=" text-center"
      nav-class="custom-nav-container mx-0 "
      :no-nav-style="true"
    >
      <custom-tab-item active lazy title="Datos">
        <report-detail
          ref="reportDetail"
          :sections="sections"
          :prior-academic-year="info.priorAcademicYear"
          :current-academic-year="info.academicYear"
          :readonly="!isNewStatus"
        />
      </custom-tab-item>
      <custom-tab-item v-if="showFilesTab" lazy title="Archivos">
        <files-tab
          :eden-dispute-resolution-id="edenDisputeResolutionId"
          :status="info.status"
        />
      </custom-tab-item>
      <custom-tab-item lazy title="Documentos de Respaldo">
        <supporting-documents-tab
          :eden-dispute-resolution-id="edenDisputeResolutionId"
          :is-readonly="isSupportingDocumentsReadonly"
        />
      </custom-tab-item>
      <custom-tab-item lazy title="Historial">
        <section-title
          class="mt-2 pt-1"
          :has-line="true"
          title="Historial"
          title-class="font-14"
        />
        <status-history-tab :eden-id="edenDisputeResolutionId" />
      </custom-tab-item>
    </custom-tab>
  </div>
</template>

<script>
import PageHeader from 'PageHeader';
import Board from 'Board';
import ActionButton from 'ActionButton';
import Service from '@/services/api/edendisputeResolution.api';
import { CustomTab, CustomTabItem } from 'custom-tabs';
import ReportDetail from './components/ReportDetail.vue';
import edenStatus from '@/utils/constants/edenStatus.js';
import FilesTab from './FilesTab.vue';
import SupportingDocumentsTab from './supporting/SupportingDocumentsTab.vue';
import DocumentServices from '@/modules/report/document';
import ReportServices from '@/modules/report/api';
import StatusHistoryTab from './history/StatusHistoryTab.vue';
import SectionTitle from 'SectionTitle';

export default {
  name: 'DisputeResolutionDetails',
  components: {
    PageHeader,
    Board,
    SectionTitle,
    ActionButton,
    CustomTab,
    CustomTabItem,
    FilesTab,
    ReportDetail,
    SupportingDocumentsTab,
    StatusHistoryTab,
  },
  data: () => ({
    info: {
      id: null,
      academicYear: null,
      priorAcademicYear: null,
      statusName: null,
      status: null,
      edenMasterId: null,
      lastUpdatedDate: null,
      createdBy: null,
    },
    sections: [],
  }),
  computed: {
    pageConfig() {
      return {
        title: 'Dispute Resolution (Querellas)',
        icon: 'fas fa-user-tie',
        routes: ['Inicio', 'Proveedor', 'Dispute Resolution (Querellas)'],
      };
    },
    detailsBoard() {
      return [
        [
          {
            label: 'Estatus:',
            icon: 'fas fa-ballot-check',
            data: this.info.statusName,
            cols: 'col-12 col-md',
          },
          {
            label: 'Año Académico',
            icon: 'fas fa-calendar-edit',
            data: this.info.academicYear,
            cols: 'col-12 col-md',
          },
          {
            label: 'Creado por:',
            icon: 'fas fa-user-check',
            data: this.info.createdBy,
            cols: 'col-12 col-md',
          },
        ],
      ];
    },
    edenDisputeResolutionId() {
      return this.$route.params.id;
    },
    isNewStatus() {
      return this.info.status === edenStatus.NEW;
    },
    showAmendButton() {
      return [
        edenStatus.GENERATED_FILE,
        edenStatus.SUBMITTED,
        edenStatus.PUBLISHED,
      ].includes(this.info.status);
    },
    showPublishButton() {
      return this.info.status === edenStatus.GENERATED_FILE;
    },
    showBuildFilesButton() {
      return this.info.status === edenStatus.SUBMITTED;
    },
    showFilesTab() {
      return [edenStatus.GENERATED_FILE, edenStatus.PUBLISHED].includes(
        this.info.status
      );
    },
    isSupportingDocumentsReadonly() {
      return this.info.status === edenStatus.PUBLISHED;
    },
  },
  async mounted() {
    await this.getDetail();
  },
  methods: {
    async getDetail() {
      const { data } = await Service.getDetail(this.edenDisputeResolutionId);
      if (data) {
        this.info = {
          id: data.id,
          edenMasterId: data.edenMasterId,
          academicYear: data.academicYear?.name,
          priorAcademicYear: data.priorAcademicYear?.name,
          statusName: data.status?.name,
          status: data.status?.id,
          lastUpdatedDate: data.lastUpdatedDate,
          createdBy: data.createdBy,
        };
        this.sections = this.mapSections(data.sections);
      }
    },
    mapSections(sections = []) {
      return sections.map((section) => ({
        ...section,
        items: (section.items || []).map((item) => ({
          ...item,
          isPriorYearEditable: item.isPriorYearEditable === true,
        })),
      }));
    },
    buildSavePayload() {
      return {
        edenDisputeResolutionMasterId: Number(this.edenDisputeResolutionId),
        items: this.sections.flatMap((section) =>
          (section.items || []).map((item) => ({
            snapshotId: item.snapshotId,
            itemId: item.itemId,
            priorYearValue: item.priorYearValue,
            currentYearValue: item.currentYearValue,
          }))
        ),
      };
    },
    validateRequiredFields() {
      if (!this.$refs.reportDetail?.validateRequiredFields) return true;
      return this.$refs.reportDetail.validateRequiredFields();
    },
    hasPendingValidations() {
      if (!this.$refs.reportDetail?.hasPendingValidations) return false;
      return this.$refs.reportDetail.hasPendingValidations();
    },
    async saveReportHandler(showToast = true) {
      await Service.saveDetail(this.buildSavePayload())
        .then(async () => {
          if (showToast) {
            this.ShowToast(
              'Confirmación',
              'Los datos del reporte han sido guardados exitosamente.',
              'success'
            );
          }
          await this.getDetail();
        })
        .catch((err) => {
          if (showToast) {
            this.ShowToast(
              '',
              err.response?.data?.message || err.message,
              'error'
            );
          }
          throw err;
        });
    },
    async handleBuildFiles() {
      const confirmed = await this.ShowCancelModal(
        'Confirmación',
        `¿Está seguro que desea generar los archivos del Año Académico ${this.info.academicYear}?`,
        'Sí',
        'No'
      );

      if (!confirmed) {
        return;
      }

      try {
        await Service.buildFiles(this.edenDisputeResolutionId);
        this.ShowToast(
          'Confirmación',
          'Los archivos se generaron correctamente.',
          'success'
        );

        await this.getDetail();
      } catch (err) {
        this.ShowToast('', err.response?.data?.message || err.message, 'error');
      }
    },
    async handleAmend() {
      const confirmed = await this.ShowCancelModal(
        'Confirmación',
        `¿Está seguro que desea enmendar este reporte?`,
        'Sí',
        'No'
      );

      if (!confirmed) {
        return;
      }

      try {
        await Service.amend(this.edenDisputeResolutionId);
        this.ShowToast(
          'Confirmación',
          'El reporte se enmendó correctamente.',
          'success'
        );

        await this.getDetail();
      } catch (err) {
        this.ShowToast('', err.response?.data?.message || err.message, 'error');
      }
    },
    async handlePublish() {
      const confirmed = await this.ShowCancelModal(
        'Confirmación',
        `¿Está seguro que desea Publicar?`,
        'Sí',
        'No'
      );

      if (!confirmed) {
        return;
      }

      try {
        await Service.publish(this.edenDisputeResolutionId);
        this.ShowToast(
          'Confirmación',
          'Los archivos se publicaron correctamente.',
          'success'
        );

        await this.getDetail();
      } catch (err) {
        this.ShowToast('', err.response?.data?.message || err.message, 'error');
      }
    },
    async handlePrint() {
      await ReportServices.getReport('edendisputeresolutiondetails', {
        DisputeResolutionMasterId: this.edenDisputeResolutionId,
      })
        .then((res) => DocumentServices.Download(res.data))
        .catch((err) => {
          this.ShowToast(
            '',
            err.response?.data?.message || err.message,
            'error'
          );
        });
    },
    async handleSubmit() {
      const isComplete = this.validateRequiredFields();
      if (!isComplete || this.hasPendingValidations()) {
        this.ShowToast(
          'Error',
          'No es posible someter el reporte. Existen validaciones pendientes de corregir, revise los indicadores de error.',
          'error'
        );
        return;
      }

      const confirmed = await this.ShowCancelModal(
        'Confirmación',
        `¿Está seguro que desea someter este reporte?`,
        'Sí',
        'No'
      );

      if (!confirmed) {
        return;
      }

      try {
        await this.saveReportHandler(false);
        await Service.submit(this.edenDisputeResolutionId);
        this.ShowToast(
          'Confirmación',
          'El reporte ha sido sometido exitosamente.',
          'success'
        );
        await this.getDetail();
      } catch (err) {
        this.ShowToast('', err.response?.data?.message || err.message, 'error');
      }
    },
    async submitReportHandler() {},
  },
};
</script>

<style lang="scss">
@import '@/assets/scss/variables/_colors.scss';
.nav-link {
  padding-left: 70px !important;
}
.no-margin {
  margin-right: 0px !important;
}

.no-tag-badge-parent {
  .tag-badge-info {
    background: none !important;
  }
  .dot {
    background-color: map-get($colors, 'green') !important;
  }
}
</style>
